import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
  type Router
} from 'vue-router'
import { handleRouterError } from '@/utils/createAsyncComponent'
import { needRefresh, requestReload } from '@/services/pwa-update'
import { queryClient } from '@/composables/queries/query_client'
import {
  FEATURE_FLAGS,
  isFeatureFlagEnabled,
  setFeatureFlag
} from '@/composables/feature-flags'
import {
  applyNativePlatformFeatureFlags,
  isNativeApp
} from '@/utils/native-platform'
import { initNavigationHistoryTracking } from '@/utils/navigation-history'
import {
  requiresAuth,
  pageTitle,
  applyBackgroundTheme
} from '@/utils/route-meta'
import { clearAuthToken, getAuthToken } from '@/services/auth-token'
import { persistentStorage } from '@/services/persistent-storage'
import { usePropertiesStore } from '@/store/properties'
import { useDismissibleAlertsStore } from '@/store/dismissible-alerts'
import { requestSignOut } from '@/composables/queries/auth'
import routesSolarMode from './solar-mode'
import routesHome from './home'
import routesInsights from './insights'
import routesSettings from './settings'
import routesAutomations from './automations'
import routesAuth from './auth'
import routesDebug from './debug'
import routesShell from './shell'

const routesArray: Array<RouteRecordRaw> = [
  ...routesSolarMode,
  ...routesHome,
  ...routesInsights,
  ...routesSettings,
  ...routesAutomations,
  ...routesAuth,
  ...routesDebug,
  ...routesShell
]

const router: Router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: routesArray
})

initNavigationHistoryTracking(router)

function redirectHandler(url: string) {
  return (window.location.href = `${
    import.meta.env.VITE_PLENTIFY_DOMAIN_URL
  }/${url}`)
}

const SOLAR_MODE_ROUTE_NAMES = new Set([
  'SolarModePersonalise',
  'SolarModeSetSunnyTimes',
  'SolarModeSetTemperatures',
  'SolarModeHotBotCalibrating',
  'SolarModeSummary',
  'SolarModeEditPreferences'
])

router.beforeEach((to, from) => {
  const fromSolar =
    from.name != null && SOLAR_MODE_ROUTE_NAMES.has(from.name as string)
  const toSolar =
    to.name != null && SOLAR_MODE_ROUTE_NAMES.has(to.name as string)
  if (fromSolar && toSolar) {
    const mergedParams = { ...from.params, ...to.params }
    const mergedQuery = { ...from.query, ...to.query }
    const paramsChanged =
      JSON.stringify(mergedParams) !== JSON.stringify(to.params)
    const queryChanged =
      JSON.stringify(mergedQuery) !== JSON.stringify(to.query)
    if (paramsChanged || queryChanged) {
      return { name: to.name, params: mergedParams, query: mergedQuery }
    }
  }
  return true
})

router.beforeEach((to) => {
  for (const flag of FEATURE_FLAGS) {
    const value = String(to.query[flag.key]).toLowerCase()
    if (value === 'true') {
      setFeatureFlag(flag.key, true)
    } else if (value === 'false') {
      setFeatureFlag(flag.key, false)
    }
  }
  // Re-assert after the query-string pass above, so a native build can't have
  // its forced-on flags (e.g. api_login) disabled by a later navigation's
  // query string - matches applyNativePlatformFeatureFlags' own contract.
  applyNativePlatformFeatureFlags()
  return true
})

router.beforeEach((to) => {
  if (!isNativeApp()) return true
  if (!requiresAuth(to)) return true
  if (getAuthToken()) return true
  return { name: 'NativeOnboarding' }
})

const SIGN_OUT_REDIRECT_PATH: Record<string, string> = {
  SettingsSignOutEverywhere: 'users/delete_session',
  SettingsSignOut: 'users/sign_in_again'
}

router.beforeEach(async (to, from) => {
  applyBackgroundTheme(from, to)

  const signOutRedirectPath =
    to.name != null ? SIGN_OUT_REDIRECT_PATH[to.name as string] : undefined
  if (signOutRedirectPath) {
    persistentStorage.removeItem('HEATING_INFO_DISMISSED')
    persistentStorage.removeItem('SETPOINT_INFO_DISMISSED')
    usePropertiesStore().currentPropertyId = undefined
    useDismissibleAlertsStore().entries = []
    queryClient.clear()

    if (isFeatureFlagEnabled('api_login')) {
      if (to.name === 'SettingsSignOutEverywhere') {
        await requestSignOut().catch(() => {})
      }
      clearAuthToken()
      return { name: 'Login' }
    }

    clearAuthToken()
    redirectHandler(signOutRedirectPath)
    return false
  }

  if (to.name === 'AddPropertyOrUnits') {
    redirectHandler('onboarding/properties')
    return false
  }
})

// NB: needs to be registered after other beforeEach hooks and kept separate..
router.beforeEach((to) => {
  if (!needRefresh.value) return true

  if (queryClient.isMutating() > 0) return true

  requestReload(to.fullPath)
  return false
})

router.afterEach((to) => {
  document.title = pageTitle(to)
})

router.onError(handleRouterError)

export { routesArray }

export default router
